import {Grid, Skeleton, Stack} from '@mui/material';
import {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {CarePlanConfiguration} from '../../../../../../../../../backend/interfaces/care-plan-configuration';
import {CarePlanConfigurationLayout} from '../../../../../../../../../backend/interfaces/care-plan-configuration-layout';
import {CarePlanActivitiesGroupApiRequestConfigurationsItem} from '../../../../../../../../../backend/requests/care-plan-activities-group';
import {
  DynamicForm,
  DynamicFormFieldShape,
} from '../../../../../../../../../components/dynamic-form';
import {ConfigurationsIndex, ConfigurationsValuesIndex} from '../../../../../../../../../types';
import {DEFAULT_FORM_VALIDATIONS} from '../../../../../../../../../utils/constants/forms';
import {convertArrayToIndex} from '../../../../../../../../../utils/functions';
import {useFetchActivitiesGroupType} from '../../../shared/use-fetch-activities-group-type';
import {useFetchActivitiesGroupTypeConfiguration} from '../../../shared/use-fetch-activities-group-type-care-plan-configuration';

export type PlannerActivityFormProps = {
  activitiesSectorId: string;
  activitiesGroupId: string;
  activitiesTypeId: string;
  initialValues?: Record<string, any>;
  onChangeValues: (values: any, activityTypeConfigurationsIndex: ConfigurationsIndex) => void;
  onChangeValid: (isValid: boolean) => void;
};

export function PlannerActivityForm({
  onChangeValid,
  onChangeValues,
  initialValues,
  ...props
}: PlannerActivityFormProps) {
  const [layout, setLayout] = useState<CarePlanConfigurationLayout | undefined>();
  const [configurationsIndex, setConfigurationsIndex] = useState<ConfigurationsIndex>();
  const [defaultValuesIndex, setDefaultValuesIndex] = useState<ConfigurationsValuesIndex>();
  const [loading, setLoading] = useState(false);
  const formInstance = useForm<DynamicFormFieldShape>({...DEFAULT_FORM_VALIDATIONS});
  const {formState, watch} = formInstance;
  const {isValid} = formState;

  // --- Watch all fields changes ---
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    const subscription = watch((values) => {
      if (!values || Object.keys(values).length === 0) return;
      // Debounce
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        onChangeValues(values, configurationsIndex);
      }, 150);
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [watch, onChangeValues, configurationsIndex]);

  // --- Watch Form isValid changes ---
  useEffect(() => {
    // Debounce
    let timeout: NodeJS.Timeout;
    timeout = setTimeout(() => {
      onChangeValid(isValid);
    }, 150);

    return () => {
      clearTimeout(timeout);
    };
  }, [isValid, onChangeValid]);

  // --- Fetch Activities Group Type data ---
  const {data: activitiesGroupTypeData, isLoading: isLoadingActivitiesGroupType} =
    useFetchActivitiesGroupType(
      props.activitiesSectorId,
      props.activitiesGroupId,
      props.activitiesTypeId
    );

  // --- Fetch Activities Group Type Configuration data ---
  const {
    items: activitiesGroupTypeConfiguration,
    isLoading: isLoadingActivitiesGroupTypeConfiguration,
  } = useFetchActivitiesGroupTypeConfiguration(
    props.activitiesSectorId,
    props.activitiesGroupId,
    props.activitiesTypeId
  );

  useEffect(() => {
    // Loading data.
    if (isLoadingActivitiesGroupType || isLoadingActivitiesGroupTypeConfiguration) {
      setLoading(true);
      setConfigurationsIndex(undefined);
      setLayout(undefined);
    }
    // Data loaded.
    else {
      setLoading(false);
      if (activitiesGroupTypeConfiguration) {
        setConfigurationsIndex(
          convertArrayToIndex<CarePlanConfiguration>(activitiesGroupTypeConfiguration, 'id')
        );
      }

      if (activitiesGroupTypeData) {
        setLayout(activitiesGroupTypeData.carePlanConfigurationLayout);
      }
    }
  }, [
    activitiesGroupTypeConfiguration,
    activitiesGroupTypeData,
    isLoadingActivitiesGroupType,
    isLoadingActivitiesGroupTypeConfiguration,
  ]);

  useEffect(
    () => {
      // Set default values if they exist.
      if (initialValues) {
        const careplanConfig = Object.entries(initialValues).map(([key, value]) => ({
          carePlanConfigurationId: key,
          carePlanConfigurationValue: value,
        }));

        const valuesIndex =
          convertArrayToIndex<CarePlanActivitiesGroupApiRequestConfigurationsItem>(
            careplanConfig,
            'carePlanConfigurationId'
          );

        setDefaultValuesIndex(valuesIndex);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // initialValues // Do it once. Ignore further changes.
    ]
  );

  if (loading) {
    return (
      <Grid container spacing={4}>
        {new Array(10).fill(1).map((_, index) => (
          <Grid item xs={6} key={`skelly_planner_create_activity_${index}`}>
            <Stack gap={2} display="flex" flexDirection="column">
              <Skeleton variant="rounded" animation="wave" width={120} height={20} />
              <Skeleton variant="rounded" animation="wave" width="100%" height={42} />
            </Stack>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <DynamicForm
      id="planner-create-activity-form"
      activityGroupId={props.activitiesGroupId}
      activityTypeId={props.activitiesTypeId}
      activitiesSectorId={props.activitiesSectorId}
      formInstance={formInstance}
      layout={layout}
      configurationsIndex={configurationsIndex}
      defaultValuesIndex={defaultValuesIndex}
      onSubmit={() => {
        console.log('> DynamicForm onSubmit triggered.');
      }}
      configuredRepetition="activity-type"
    />
  );
}
